'use client';

import { createStore } from 'zustand';

import { getLogger } from '@/logger/logger';
import { addCartItems } from '@/services/cart/add-cart-items.service';
import { changeCartPatient } from '@/services/cart/change-cart-patient.service';
import { clearCart } from '@/services/cart/clear-cart.service';
import { getActiveCart } from '@/services/cart/get-active-cart.service';
import { removeCartItems } from '@/services/cart/remove-cart-items.service';
import { Cart, CartItem, CartItemBody, CartPatient, CartPurchaseTrack } from '@/types/cart.types';
import { CartAddIntent, CartClearReason } from '@/types/cart-clear-confirmation.types';
import { CartOrigin } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { buildPurchaseTrack } from '@/utils/cart/build-purchase-track.util';
import { trackCartOpened } from '@/utils/cart/cart-analytics.util';

const logger = getLogger('cart-store');

export type CartFetchStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type CartState = {
  isOpen: boolean;
  cart: Cart | null;
  status: CartFetchStatus;
  origin: CartOrigin;
};

const initialState: CartState = {
  isOpen: false,
  cart: null,
  status: 'idle',
  origin: CartOrigin.OTHER,
};

export const cartStore = createStore<CartState>(() => ({ ...initialState }));

let pendingLoad: Promise<void> | null = null;
let pendingLoadAbort: AbortController | null = null;
let pendingLoadIsDrawerOwned = false;
let pendingLoadIsRefresh = false;
let hasUnreportedOpening = false;

const abortPendingLoad = () => {
  if (!pendingLoad) {
    return;
  }

  pendingLoadAbort?.abort();
  pendingLoad = null;
  pendingLoadAbort = null;
  pendingLoadIsDrawerOwned = false;
  pendingLoadIsRefresh = false;

  const { status, cart } = cartStore.getState();

  if (status === 'loading') {
    cartStore.setState({ status: cart ? 'loaded' : 'idle' });
  }
};

const abortDrawerLoad = () => {
  if (!pendingLoadIsDrawerOwned) {
    return;
  }

  abortPendingLoad();
};

const startLoad = (isDrawerOwned: boolean, isRefresh = false): Promise<void> => {
  if (pendingLoad && !isRefresh && !pendingLoadIsRefresh) {
    return pendingLoad;
  }

  abortPendingLoad();

  const abortController = new AbortController();
  const { signal } = abortController;

  pendingLoadAbort = abortController;
  pendingLoadIsDrawerOwned = isDrawerOwned;
  pendingLoadIsRefresh = isRefresh;

  if (cartStore.getState().status !== 'loaded') {
    cartStore.setState({ status: 'loading' });
  }

  const load = getActiveCart(signal)
    .then((cart) => {
      if (signal.aborted) {
        return;
      }

      cartStore.setState({ cart, status: 'loaded' });

      if (hasUnreportedOpening && cartStore.getState().isOpen) {
        hasUnreportedOpening = false;
        trackCartOpened(cartStore.getState().origin, cart?.items ?? []);
      }
    })
    .catch((error) => {
      if (signal.aborted) {
        return;
      }

      logger.error('Failed to fetch the active cart', error);

      if (!isRefresh) {
        cartStore.setState({ status: 'error' });
      }
    })
    .finally(() => {
      if (pendingLoad === load) {
        pendingLoad = null;
        pendingLoadAbort = null;
        pendingLoadIsDrawerOwned = false;
        pendingLoadIsRefresh = false;
      }
    });

  pendingLoad = load;

  return load;
};

export const loadCart = (): Promise<void> => startLoad(false);

const refreshCart = (): Promise<void> => startLoad(false, true);

export interface CartReadResult {
  cart: Cart | null;
  wasRead: boolean;
}

export const ensureCartLoaded = async (): Promise<CartReadResult> => {
  if (cartStore.getState().status !== 'loaded') {
    await loadCart();
  }

  const { cart, status } = cartStore.getState();

  return { cart, wasRead: status === 'loaded' };
};

export const openCart = (origin: CartOrigin) => {
  cartStore.setState({ isOpen: true, origin });
  hasUnreportedOpening = true;

  void startLoad(true);
};

export const retryCart = (): Promise<void> => {
  cartStore.setState({ isOpen: true });
  hasUnreportedOpening = true;

  return startLoad(true);
};

export const MIXED_CART_BATCH_ERROR_MESSAGE
  = 'A standalone guide cannot be published together with scheduled items: send one batch for each.';

export const EMPTY_CART_BATCH_ERROR_MESSAGE = 'An empty batch has nothing to publish and would wipe the cart.';

const isMixedBatch = (items: CartItemBody[]): boolean =>
  items.some((item) => !item.slotId) && items.some((item) => Boolean(item.slotId));

const hasStandaloneItem = (cart: Cart | null): boolean =>
  cart?.items.some((item) => !item.slot.id) ?? false;

export const getCartClearReason = (cart: Cart | null, intent: CartAddIntent): CartClearReason | null => {
  const cartHasItems = (cart?.items.length ?? 0) > 0;

  if (intent.isStandalone && cartHasItems) {
    return CartClearReason.ADDING_STANDALONE;
  }

  if (hasStandaloneItem(cart)) {
    return CartClearReason.CART_HAS_STANDALONE;
  }

  return null;
};

export const toCartAddIntent = (items: CartItemBody[]): CartAddIntent => ({
  isStandalone: items.some((item) => !item.slotId),
});

const shouldClearBeforeAdding = (cart: Cart | null, items: CartItemBody[]): boolean =>
  getCartClearReason(cart, toCartAddIntent(items)) !== null;

export interface AddItemsToCartResult {
  cart: Cart | null;
  purchaseTrack?: CartPurchaseTrack;
}

export const addItemsToCart = async (
  items: CartItemBody[],
  origin: CartOrigin,
): Promise<AddItemsToCartResult> => {
  if (items.length === 0) {
    throw new Error(EMPTY_CART_BATCH_ERROR_MESSAGE);
  }

  if (isMixedBatch(items)) {
    throw new Error(MIXED_CART_BATCH_ERROR_MESSAGE);
  }

  await ensureCartLoaded();

  const purchaseTrack = await buildPurchaseTrack();

  if (shouldClearBeforeAdding(cartStore.getState().cart, items)) {
    await clearCart();
  }

  abortPendingLoad();

  const cart = await addCartItems({ items, purchaseTrack });

  cartStore.setState({ cart, status: 'loaded', isOpen: true, origin });
  hasUnreportedOpening = false;
  trackCartOpened(origin, cart?.items ?? []);

  return { cart, purchaseTrack };
};

const restoreCartItems = (previousItems: CartItem[], removedIds: ReadonlySet<string>) => {
  const { cart } = cartStore.getState();

  if (!cart) {
    return;
  }

  const currentItemsById = new Map(cart.items.map((item) => [item.id, item]));
  const previousIds = new Set(previousItems.map((item) => item.id));

  const items = [
    ...previousItems
      .filter((item) => removedIds.has(item.id) || currentItemsById.has(item.id))
      .map((item) => currentItemsById.get(item.id) ?? item),
    ...cart.items.filter((item) => !previousIds.has(item.id)),
  ];

  cartStore.setState({ cart: { ...cart, items } });
};

export const removeItemsFromCart = async (cartItemsIds: string[]): Promise<Cart | null> => {
  abortPendingLoad();

  const previousCart = cartStore.getState().cart;
  const previousItems = previousCart?.items ?? [];
  const removedIds = new Set(cartItemsIds);

  if (previousCart) {
    cartStore.setState({
      cart: {
        ...previousCart,
        items: previousCart.items.filter((item) => !removedIds.has(item.id)),
      },
    });
  }

  try {
    await removeCartItems(cartItemsIds);
  } catch (error) {
    restoreCartItems(previousItems, removedIds);

    await refreshCart();

    throw error;
  }

  await refreshCart();

  return cartStore.getState().cart;
};

export const changePatientOnCartItems = async (
  cartItemsIds: string[],
  patient: CartPatient,
): Promise<Cart | null> => {
  abortPendingLoad();

  const previousCart = cartStore.getState().cart;
  const changedIds = new Set(cartItemsIds);

  if (previousCart) {
    cartStore.setState({
      cart: {
        ...previousCart,
        items: previousCart.items.map((item) => (changedIds.has(item.id) ? { ...item, patient } : item)),
      },
    });
  }

  try {
    await changeCartPatient(cartItemsIds.map((cartItemId) => ({ cartItemId, patientId: patient.id })));
  } catch (error) {
    if (previousCart) {
      cartStore.setState({ cart: previousCart });
    }

    await refreshCart();

    throw error;
  }

  await refreshCart();

  return cartStore.getState().cart;
};

export const closeCart = () => {
  abortDrawerLoad();
  hasUnreportedOpening = false;
  cartStore.setState({ isOpen: false });
};

export const setCartOpen = (isOpen: boolean, origin: CartOrigin = CartOrigin.OTHER) => {
  if (isOpen) {
    openCart(origin);
    return;
  }

  closeCart();
};

export const resetCart = () => {
  abortPendingLoad();
  hasUnreportedOpening = false;
  cartStore.setState({ ...initialState });
};
